import type { PartialColor } from "./types";

// AdaptiveX Design System Colors
export const adaptivexPrimary = {
	50: "#f3f0ff",
	100: "#e9e2ff",
	200: "#d6c7ff",
	300: "#b8a0ff",
	400: "#9570ff",
	500: "#5F2BFF", // Primary Solid
	600: "#5A00FF", // Primary End
	700: "#3B0B98", // Primary Start
	800: "#2d0770",
	900: "#250558",
	950: "#160333",
} satisfies PartialColor;

export const adaptivexInk = {
	50: "#f8f8f9",
	100: "#f0f0f2",
	200: "#e1e1e5",
	300: "#c9c9d1",
	400: "#a8a8b3",
	500: "#8a8a96",
	600: "#6b6b7a",
	700: "#4a4a5a",
	800: "#2d2d3a",
	900: "#1a1a24",
	950: "#0D0D0F", // Ink / Title
} satisfies PartialColor;

export const adaptivexBody = {
	50: "#f9fafb",
	100: "#f3f4f6",
	200: "#e5e7eb",
	300: "#d1d5db",
	400: "#9ca3af",
	500: "#6b7280",
	600: "#4b5563",
	700: "#374151",
	800: "#1f2937", // Body Text
	900: "#111827",
	950: "#0f172a",
} satisfies PartialColor;

export const adaptivexBackground = {
	50: "#ffffff", // Page Background
	100: "#f5f5f7", // Subtle Section
	200: "#e5e7eb", // Divider/Keyline
	300: "#d1d5db",
	400: "#9ca3af",
	500: "#6b7280",
	600: "#4b5563",
	700: "#374151",
	800: "#1f2937",
	900: "#111827",
	950: "#0f172a",
} satisfies PartialColor;

export const adaptivexSuccess = {
	50: "#ecfdf5",
	100: "#d1fae5",
	200: "#a7f3d0",
	300: "#6ee7b7",
	400: "#34d399",
	500: "#10B981", // Success
	600: "#059669",
	700: "#047857",
	800: "#065f46",
	900: "#064e3b",
	950: "#022c22",
} satisfies PartialColor;

export const adaptivexWarning = {
	50: "#fffbeb",
	100: "#fef3c7",
	200: "#fde68a",
	300: "#fcd34d",
	400: "#fbbf24",
	500: "#F59E0B", // Warning
	600: "#d97706",
	700: "#b45309",
	800: "#92400e",
	900: "#78350f",
	950: "#451a03",
} satisfies PartialColor;

export const adaptivexError = {
	50: "#fef2f2",
	100: "#fee2e2",
	200: "#fecaca",
	300: "#fca5a5",
	400: "#f87171",
	500: "#EF4444", // Error
	600: "#dc2626",
	700: "#b91c1c",
	800: "#991b1b",
	900: "#7f1d1d",
	950: "#450a0a",
} satisfies PartialColor;

export const adaptivexInfo = {
	50: "#eff6ff",
	100: "#dbeafe",
	200: "#bfdbfe",
	300: "#93c5fd",
	400: "#60a5fa",
	500: "#3B82F6", // Info
	600: "#2563eb",
	700: "#1d4ed8",
	800: "#1e40af",
	900: "#1e3a8a",
	950: "#172554",
} satisfies PartialColor;

export const california = {
	50: "#fffaea",
	100: "#fff3c6",
	200: "#ffe587",
	300: "#ffd049",
	400: "#ffbb1f",
	500: "#fb9c0c",
	600: "#de7101",
	700: "#b84d05",
	800: "#953b0b",
	900: "#7b310c",
	950: "#471701",
} satisfies PartialColor;

export const chateauGreen = {
	50: "#edfcf2",
	100: "#d2f9de",
	200: "#aaf0c4",
	300: "#72e3a3",
	400: "#3acd7e",
	500: "#16b364",
	600: "#0a9150",
	700: "#087442",
	800: "#095c37",
	900: "#094b2f",
	950: "#032b1a",
} satisfies PartialColor;

export const kepple = {
	50: "#f0fdfa",
	100: "#ccfbef",
	200: "#9af5e1",
	300: "#5fe9ce",
	400: "#2ed3b8",
	500: "#15b79f",
	600: "#0e9382",
	700: "#107569",
	800: "#115e56",
	900: "#134e48",
	950: "#042f2c",
} satisfies PartialColor;

export const neonBlue = {
	50: "#ecf0ff",
	100: "#dde3ff",
	200: "#c2cbff",
	300: "#9ca7ff",
	400: "#7578ff",
	500: "#635bff",
	600: "#4e36f5",
	700: "#432ad8",
	800: "#3725ae",
	900: "#302689",
	950: "#1e1650",
} satisfies PartialColor;

export const nevada = {
	50: "#fbfcfe",
	100: "#f0f4f8",
	200: "#dde7ee",
	300: "#cdd7e1",
	400: "#9fa6ad",
	500: "#636b74",
	600: "#555e68",
	700: "#32383e",
	800: "#202427",
	900: "#121517",
	950: "#090a0b",
} satisfies PartialColor;

export const redOrange = {
	50: "#fef3f2",
	100: "#fee4e2",
	200: "#ffcdc9",
	300: "#fdaaa4",
	400: "#f97970",
	500: "#f04438",
	600: "#de3024",
	700: "#bb241a",
	800: "#9a221a",
	900: "#80231c",
	950: "#460d09",
} satisfies PartialColor;

export const royalBlue = {
	50: "#ecf3ff",
	100: "#dce8ff",
	200: "#c0d4ff",
	300: "#9bb6ff",
	400: "#738dff",
	500: "#5265ff",
	600: "#3339f8",
	700: "#3739de",
	800: "#2225b1",
	900: "#24298b",
	950: "#151651",
} satisfies PartialColor;

export const shakespeare = {
	50: "#ecfdff",
	100: "#cff7fe",
	200: "#a4eefd",
	300: "#66e0fa",
	400: "#10bee8",
	500: "#04aad6",
	600: "#0787b3",
	700: "#0d6d91",
	800: "#145876",
	900: "#154964",
	950: "#082f44",
} satisfies PartialColor;

export const stormGrey = {
	50: "#f9fafb",
	100: "#f1f1f4",
	200: "#dcdfe4",
	300: "#b3b9c6",
	400: "#8a94a6",
	500: "#667085",
	600: "#565e73",
	700: "#434a60",
	800: "#313749",
	900: "#212636",
	950: "#121621",
} satisfies PartialColor;

export const tomatoOrange = {
	50: "#fff3ed",
	100: "#ffe2d4",
	200: "#ffc1a8",
	300: "#ffa280",
	400: "#ff9771",
	500: "#ff6c47",
	600: "#fe4011",
	700: "#ed3507",
	800: "#9f2c0f",
	900: "#7e1110",
	950: "#440608",
} satisfies PartialColor;
